import { Product, ProductType, School, Country, Currency, DiscountValueType, DiscountActionTargetTypeEnum, Centre, BookingSystem, DiscountBandTypeEnum, CourseIntensityDto } from "app/shared/model";
import { ProductOffering } from "app/shared/model/ProductOffering";
import { AgentSearchResult, AgentOffice, Agent } from "app/shared/model/Agent";
import { ProductFilterType, ProductFilter } from "app/shared/model/ProductFilter";
import { Fee } from "app/shared/model/Fee";
import { SavedDiscountBand, SavedDiscountAction, EditableBandedDiscount, DiscountTarget } from "app/pages/DiscountEditor/modules/DiscountActions";
import { DiscountType } from "app/shared/model/DiscountType";
import { DiscountDto } from "app/shared/model/DiscountDto";
import { SuccessfulSaveDiscountResult, InvalidSaveDiscountResult } from "app/shared/model/SaveDiscountResultDto";
import { CountrySelectionMode } from "app/shared/model/CountrySelectionMode";
import { AgentSelectionMode } from "app/shared/model/AgentSelectionMode";
import { UnitsFilterType, UnitsFilter } from "app/shared/model/ProductUnitFilter";
import { SavedBookingSystemCriteria, SavedStudyStartDateCriteria } from "app/pages/DiscountEditor/modules/AdditionalCriteria/state";

export enum ActionType {
    PageLoaded = 'DiscountEditor/PageLoaded',
    AddOfferingsForSchool = 'DiscountEditor/AddOfferingsForSchool',
    OfferingsLoadStarted = 'DiscountEditor/OfferingsLoadStarted',
    OfferingsLoadError = 'DiscountEditor/OfferingsLoadError',
    OfferingsInvalidateForSchools = 'DiscountEditor/OfferingsInvalidateForSchools',
    ChangeProductFilter = 'DiscountEditor/ChangeProductFilter',
    ChangeSchoolGroupOpenState = 'DiscountEditor/ChangeSchoolGroupOpenState',
    ChangeSchoolGroupSelection = 'DiscountEditor/ChangeSchoolGroupSelection',
    ChangeCentreGroupOpenState = 'DiscountEditor/ChangeCentreGroupOpenState',
    ChangeCentreGroupSelection = 'DiscountEditor/ChangeCentreGroupSelection',
    ChangeSchoolSelection = 'DiscountEditor/ChangeSchoolSelection',
    SetDate = 'DiscountEditor/DateChanged',
    DateNeverExpires = 'DiscountEditor/DateNeverExpires',
    ChangePublicName = "DiscountEditor/ChangePublicName", 
    ChangeInternalName = "DiscountEditor/ChangeInternalName", 
    ChangeDescription = "DiscountEditor/ChangeDescription", 
    ChangePromotionCode = "DiscountEditor/ChangePromotionCode",
    ChangeDiscountType = "DiscountEditor/ChangeDiscountType", 
    ChangeUnitFilter = 'DiscountEditor/ChangeUnitFilter',
    AgentSearchChanged = 'DiscountEditor/AgentSearchChanged',
    AgentSearchFailed = 'DiscountEditor/AgentSearchFailed',
    AgentSearchResultsLoaded = 'DiscountEditor/AgentSearchResultsLoaded',
    AgentSearchResultSelected = 'DiscountEditor/AgentSearchResultSelected',
    ChangeAgentSelectionMode = 'DiscountEditor/ChangeAgentSelectionMode',
    ChangeAgentSelection = 'DiscountEditor/ChangeAgentSelection',
    AgentOfficesLoaded = 'DiscountEditor/AgentOfficesLoaded',
    AgentOfficesLoadFailed = 'DiscountEditor/AgentOfficesLoadFailed',
    ShowOrHideAgentOffices = 'DiscountEditor/ShowOrHideAgentOffices',
    ChangeAgentOfficeSelection = 'DiscountEditor/ChangeAgentOfficeSelection',
    ChangeCountrySelectionMode = 'DiscountEditor/ChangeCountrySelectionMode',
    AddCountrySelection = 'DiscountEditor/AddCountrySelection',
    ChangeCountrySelection = 'DiscountEditor/ChangeCountrySelection',
    StartAddingDiscountAction = 'DiscountEditor/StartAddingDiscountAction',
    StartEditingDiscountAction = "DiscountEditor/StartEditingDiscountAction",
    ShowAddDiscountActionWizard = "DiscountEditor/ShowAddDiscountActionWizard",
    ShowDeleteDiscountActionConfirmation = 'DiscountEditor/ShowDeleteDiscountActionConfirmation', 
    CancelShowDeleteDiscountActionConfirmation = 'DiscountEditor/CancelShowDeleteDiscountActionConfirmation',
    CancelAddOrUpdateDiscountAction = 'DiscountEditor/ChancelAddOrUpdateDiscountAction',
    UpdateEditableDiscountAction = 'DiscountEditor/UpdateEditableDiscountAction',
    ChangeDiscountActionValueType = 'DiscountEditor/ChangeDiscountActionValueType',
    DeleteDiscountAction = 'DiscountEditor/DeleteDiscountAction',
    SaveDiscountAction = 'DiscountEditor/SaveDiscountAction',
    SaveStarted = 'DiscountEditor/SaveStarted',
    SaveSuccessful = 'DiscountEditor/SaveSuccessful',
    SaveFailed = 'DiscountEditor/SaveFailed',
    SaveInvalid = 'DiscountEditor/SaveInvalid',
    SetAddOrEditCriteriaModal = 'DiscountEditor/SetAddOrEditCriteriaModal',
    SaveStudentStartDateCriteria = 'DiscountEditor/SaveStudentStartDateCriteria',
    SetAdditionalCriteriaProductFilter = 'DiscountEditor/SetAdditionalCriteriaProductFilter',
    SaveBookingSystemsCriteria = 'DiscountEditor/SaveBookingSystemsCriteria',
    DeleteAdditionalCriteria = 'DiscountEditor/DeleteAdditionalCriteria',
    SetDeleteCriteriaConfirmationModal = 'DiscountEditor/SetDeleteCriteriaConfirmation',
    SetSelectedDivisions = 'discountEditor/divisions/SET_SELECTED_DIVISIONS',
    FeesLoaded = 'DiscountEditor/FeesLoaded',
    CatalogProductsLoaded = 'DiscountEditor/CatalogProductsLoaded',
    Other = '__OTHER__'
}

export type PageLoadedEvent = {
    type: ActionType.PageLoaded
    discount: DiscountDto
    data: PageLoadedData
}

export type PageLoadedData = {
    id: string|undefined
    products: Product[],
    centres: Centre[],
    schools: School[],
    countries: Country[], 
    currencies: Currency[],
    bookingSystems: BookingSystem[],
    offerings: ProductOffering[],
    fees: Fee[]
    agents: Agent[],
    courseIntensity: CourseIntensityDto[],
}

export type AddOfferingsForSchool = {
    type: ActionType.AddOfferingsForSchool,
    offerings: ProductOffering[]
}

export type ChangeSchoolGroupSelection = {
    type: ActionType.ChangeSchoolGroupSelection
    isSelected: boolean
    code: string
}

export type ChangeCentreGroupSelection = {
    type: ActionType.ChangeCentreGroupSelection
    isSelected: boolean
    centreCode: string
}

export type ChangeCentreGroupOpenState = {
    type: ActionType.ChangeCentreGroupOpenState
    isOpen: boolean
    centreCode: string
}

export type ChangeSchoolGroupOpenState = {
    type: ActionType.ChangeSchoolGroupOpenState
    isOpen: boolean
    code: string
}

export type ChangeSchoolSelection = {
    type: ActionType.ChangeSchoolSelection
    isSelected: boolean
    code: string
}

export type DateChangedAction = {
    type: ActionType.SetDate, 
    dateType: DateType, 
    dateField: DateRangeField,
    value: string, 
}

export type DateNeverExpiresAction = {
    type: ActionType.DateNeverExpires, 
    dateType: DateType, 
    value: boolean
}

export type AgentSearchTermChangedAction = {
    type: ActionType.AgentSearchChanged,
    searchTerm: string
}

export type AgentSearchFailed = {
    type: ActionType.AgentSearchFailed
}

export type AgentSearchResultsLoadedAction = {
    type: ActionType.AgentSearchResultsLoaded,
    searchTerm: string,
    results: AgentSearchResult[]
}

export type AgentSearchResultSelected = {
    type: ActionType.AgentSearchResultSelected,
    agent: AgentSearchResult
}

export type ChangeAgentSelection = {
    type: ActionType.ChangeAgentSelection,
    id: string,
    isSelected: boolean
}

export type AgentOfficesLoaded = {
    type: ActionType.AgentOfficesLoaded,
    agentId: string,
    isSelected: boolean,
    offices: AgentOffice[]
}

export type AgentOfficesLoadFailedAction = {
    type: ActionType.AgentOfficesLoadFailed,
    agentId: string,
}

export type ShowOrHideAgentOffices = {
    type: ActionType.ShowOrHideAgentOffices,
    agentId: string,
    show: boolean
}

export type ChangeAgentOfficeSelection = {
    type: ActionType.ChangeAgentOfficeSelection,
    agentId: string,
    officeId: string,
    isSelected: boolean,
    isAgentSelected: boolean|undefined
}

export type ChangeAgentSelectionMode = {
    type: ActionType.ChangeAgentSelectionMode,
    mode: AgentSelectionMode
}

export type AddCountrySelection = {
    type: ActionType.AddCountrySelection,
    countryId: string
}

export type ChangeAllowAnyCountry = {
    type: ActionType.ChangeCountrySelectionMode,
    mode: CountrySelectionMode
}

export type ChangeCountrySelection = {
    type: ActionType.ChangeCountrySelection,
    countryId: string,
    isSelected: boolean
}

export type OfferingsLoadStarted = {
    type: ActionType.OfferingsLoadStarted,
    schoolIds: string[]
}

export type OfferingsLoadError = {
    type: ActionType.OfferingsLoadError,
    schoolIds: string[]
    error: string
}

export type OfferingsInvalidateForSchools = {
    type: ActionType.OfferingsInvalidateForSchools,
    schoolIds: string[]
}

export type ChangeProductFilter = {
    type: ActionType.ChangeProductFilter,
    productFilter: ProductFilter
}

export type ChangeDiscountType = {
    type: ActionType.ChangeDiscountType
    discountType: DiscountType
}

export type ChangePublicNameAction = {
    type: ActionType.ChangePublicName, 
    value: string, 
}

export type ChangeInternalNameAction = {
    type: ActionType.ChangeInternalName, 
    value: string, 
}

export type ChangeDescriptionAction = {
    type: ActionType.ChangeDescription,
    value: string 
}

export type ChangePromotionCodeAction = {
    type: ActionType.ChangePromotionCode,
    value: string
}

export type SetUnitFilter = {
    type: ActionType.ChangeUnitFilter,
    filter: UnitsFilter
}

export type StartAddingDiscountAction = {
    type: ActionType.StartAddingDiscountAction
    target: DiscountActionTargetTypeEnum
    bandType: DiscountBandTypeEnum
    initialTarget?: DiscountTarget
}

export type StartEditingDiscountAction = {
    type: ActionType.StartEditingDiscountAction
    discountAction: SavedDiscountAction
}

export type UpdateEditableDiscountAction = {
    type: ActionType.UpdateEditableDiscountAction,
    discountAction: EditableBandedDiscount
}

export type ChangeDiscountActionValueTypeAction = {
    type: ActionType.ChangeDiscountActionValueType,
    valueType: DiscountValueType
}

export type DeleteDiscountAction = {
    type: ActionType.DeleteDiscountAction, 
    id : string
}

export type ShowAddDiscountActionWizard = {
    type: ActionType.ShowAddDiscountActionWizard
}

export type ShowDeleteDiscountActionConfirmation = {
    type: ActionType.ShowDeleteDiscountActionConfirmation
    discountAction: SavedDiscountAction
}

export type CancelShowDeleteDiscountActionConfirmation = {
    type: ActionType.CancelShowDeleteDiscountActionConfirmation
}

export type CancelAddOrUpdateDiscountAction = {
    type: ActionType.CancelAddOrUpdateDiscountAction
}

export type SaveDiscountAction = {
    type: ActionType.SaveDiscountAction,
    discountAction: EditableBandedDiscount
}

export type SetAddOrEditCriteriaModal = {
    type: ActionType.SetAddOrEditCriteriaModal,
    modal: AdditionalCriteriaType
}

export type SaveStudentStartDateCriteria = {
    type: ActionType.SaveStudentStartDateCriteria, 
    criteria: SavedStudyStartDateCriteria
}

export type SaveBookingSystemsCriteria = {
    type: ActionType.SaveBookingSystemsCriteria, 
    criteria: SavedBookingSystemCriteria
}

export type DeleteAdditionalCriteria = {
    type: ActionType.DeleteAdditionalCriteria, 
    criteriaType: AdditionalCriteriaType
}

export type SetDeleteCriteriaConfirmationModal = {
    type: ActionType.SetDeleteCriteriaConfirmationModal,
    criteriaType: AdditionalCriteriaType
}

export type SetSelectedDivisionsAction = {
    type: ActionType.SetSelectedDivisions,
    ids?: number[] | number
}

export type FeesLoadedAction = {
    type: ActionType.FeesLoaded,
    fees: Fee[]
}

export type CatalogProductsLoadedAction = {
    type: ActionType.CatalogProductsLoaded,
    products: Product[]
    offerings?: ProductOffering[]
    loadedSchoolCodes?: string[]
}

export type OtherAction = {
    type: ActionType.Other
}

export enum AdditionalCriteriaType {
    None, 
    StudyStartDate, 
    BookingSystem, 
    Course, 
    Accommodation, 
    Transfer, 
    AddOns
}

export enum DateType {
    Booking, 
    Product
}

export enum DateRangeField {
    To, 
    From
}

export type SaveStartedAction = {
    type: ActionType.SaveStarted
    isNew: boolean
}

export type SaveFailedAction = {
    type: ActionType.SaveFailed
}

export type SaveSuccessAction = {
    type: ActionType.SaveSuccessful,
    isNew: boolean
    result: SuccessfulSaveDiscountResult
}

export type SaveInvalidAction = {
    type: ActionType.SaveInvalid,
    result: InvalidSaveDiscountResult
}

export type SetAdditionalCriteriaProductFilterAction = {
    type: ActionType.SetAdditionalCriteriaProductFilter,
    productFilter: ProductFilter
    unitsFilter: UnitsFilter
}

export type Action =
    PageLoadedEvent
    | AddOfferingsForSchool
    | ChangeProductFilter
    | ChangeSchoolGroupOpenState
    | ChangeSchoolGroupSelection
    | ChangeCentreGroupOpenState
    | ChangeCentreGroupSelection
    | ChangeSchoolSelection
    | DateChangedAction
    | DateNeverExpiresAction
    | ChangePublicNameAction
    | ChangeInternalNameAction
    | ChangeDiscountType
    | SetUnitFilter
    | ChangeDescriptionAction
    | ChangePromotionCodeAction
    | OtherAction
    | OfferingsLoadStarted
    | OfferingsLoadError
    | OfferingsInvalidateForSchools
    | AgentSearchTermChangedAction
    | AgentSearchFailed
    | ChangeAgentSelectionMode
    | AgentSearchResultsLoadedAction
    | AgentSearchResultSelected
    | ChangeAgentSelection
    | AgentOfficesLoaded
    | AgentOfficesLoadFailedAction
    | ShowOrHideAgentOffices
    | ChangeAgentOfficeSelection
    | ChangeAllowAnyCountry
    | AddCountrySelection
    | ChangeCountrySelection
    | StartAddingDiscountAction
    | StartEditingDiscountAction 
    | ShowAddDiscountActionWizard
    | ShowDeleteDiscountActionConfirmation
    | CancelShowDeleteDiscountActionConfirmation
    | UpdateEditableDiscountAction
    | ChangeDiscountActionValueTypeAction
    | DeleteDiscountAction
    | CancelAddOrUpdateDiscountAction    
    | SaveDiscountAction
    | SaveStartedAction
    | SaveFailedAction
    | SaveSuccessAction
    | SaveInvalidAction
    | SetAddOrEditCriteriaModal
    | SaveStudentStartDateCriteria
    | SetAdditionalCriteriaProductFilterAction
    | SaveBookingSystemsCriteria
    | DeleteAdditionalCriteria
    | SetDeleteCriteriaConfirmationModal
    | SetSelectedDivisionsAction
    | FeesLoadedAction
    | CatalogProductsLoadedAction

